import {
  EAbsorbPointType,
  EAbsorbSource,
  ESublineType,
  IAbsorbPointInfo,
  IWallAbsorbUniformStore
} from '@/editor/types/absorb';
import { ICalculateOptions, WallPointAbsorbCalculator } from './wallPointAbsorbCalculator';
import SublineModel from '@/editor/entities/2d/sublineModel';
import { WallAbsorbPoint } from '@/editor/entities/2d/wallAbsorbPoint2';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { EModifierKey } from '@/editor/stores/keyboardStore';
import { globalStores } from '@/editor/stores';
import { modelManager } from '../../entityManager';
import { getExtremePointNormalLine, getOffsetLineSegByPoint, getOrthogonalPoint } from './utils';
import { SCENE_SIZE } from '@/editor/constant/scene';
import { pixelRatio2d } from '@/editor/math/const';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { Circle2d } from '@/editor/math/circle2d';
import { EEntityType } from '@/editor/types';
import { getRoomId } from '@/editor/data/room/utils';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';

type TWallType = EEntityType.Wall | EEntityType.SeparateWall;

export class WallPointAbsorbManager {
  private _type: TWallType;
  private _calculator: WallPointAbsorbCalculator;
  private _sublineModel = new SublineModel();
  private _wallAbsorbPoint = new WallAbsorbPoint();
  private _separateWallRoomId: string | null = null;
  private _separateWallRoomLocked = false;
  private _eventDisposeSet: Set<() => void> = new Set();
  private _zoom: number;

  constructor(wallStore: IWallAbsorbUniformStore, type: TWallType = EEntityType.Wall) {
    this._calculator = new WallPointAbsorbCalculator(wallStore, type);
    this._type = type;
    this._zoom = globalStores.currentSceneZoom;
    this._eventDisposeSet
      .add(editorEvent.on(EditorEventName.ZOOM_UPDATE_2D, this._setZoom))
      .add(editorEvent.on(EditorEventName.ZOOM_UPDATE_3D, this._setZoom));
  }

  private _setZoom = () => {
    if (globalStores.currentSceneZoom !== this._zoom) {
      this._zoom = globalStores.currentSceneZoom;
      this._wallAbsorbPoint.updateZoom(this._zoom);
    }
  };

  updateWallData() {
    if (this._type === EEntityType.SeparateWall) {
      this._calculator.updateWallData({ roomIds: this._separateWallRoomId ? [this._separateWallRoomId] : [] });
      return;
    }

    this._calculator.updateWallData();
  }

  lockSeparateWallRoom(roomId?: string) {
    if (this._type !== EEntityType.SeparateWall || !roomId) return;

    this._separateWallRoomId = roomId;
    this._separateWallRoomLocked = true;
    this.updateWallData();
  }

  clearSeparateWallRoom() {
    if (this._type !== EEntityType.SeparateWall) return;

    this._separateWallRoomId = null;
    this._separateWallRoomLocked = false;
    this.updateWallData();
  }

  setOrthogonalData(point: IPoint2d) {
    this._calculator.setOrthogonalData(point);
  }
  clearOrthogonalData() {
    this._calculator.clearOrthogonalData();
  }

  calculateAbsorbPointThenRender(rawPoint: IPoint2d) {
    this.clearModel();

    // shift 键强制吸附正交线
    const calculateOptions: ICalculateOptions = {};
    const currentPoint = rawPoint;
    const forceOrthogonalAbsorb = globalStores.keyboardStore.isKeyPressed(EModifierKey.Shift);
    if (forceOrthogonalAbsorb && this._calculator.orthogonalOriginPoint) {
      calculateOptions.forceOrthogonalAbsorb = true;
      rawPoint = getOrthogonalPoint(this._calculator.orthogonalOriginPoint, rawPoint); // 将rawPoint投影到正交线上
    }

    if (this._type === EEntityType.Wall) {
      calculateOptions.extraWallInfos = this._calculateDxfAdsorb(rawPoint);
    } else if (this._type === EEntityType.SeparateWall) {
      this._updateSeparateWallData(currentPoint);
    }

    const absorbPointInfo = this._calculator.calculate(rawPoint, calculateOptions);
    if (!absorbPointInfo) return;

    this.updateModel(absorbPointInfo);

    return absorbPointInfo.absorbPoint;
  }

  private _dxfAdsorbSegHandler(dxfLine: number[] | undefined) {
    if (!dxfLine) return undefined;
    const start = { x: dxfLine[0], y: dxfLine[1] };
    const end = { x: dxfLine[2], y: dxfLine[3] };
    const seg = new LineSeg2d(start, end);
    if (seg.length > 200) {
      // TODO: 待优化，最好在dxf解析时过滤掉
      const seg = new LineSeg2d(start, end);
      const [startNormalLine, endNormalLine] = getExtremePointNormalLine(seg);
      return [
        {
          id: '',
          start,
          end,
          seg,
          shape: seg.createLine(),
          source: EAbsorbSource.FloorPlan,
          startNormalLine,
          endNormalLine
        }
      ];
    }
    return undefined;
  }

  private _calculateDxfAdsorb(rawPoint: IPoint2d) {
    if (globalStores.paramStore.floorPlanConfig && globalStores.paramStore.floorPlanConfig.type === 'dxf') {
      const dxfLine = modelManager.floorPlanManager.getNearestLine(
        rawPoint.x,
        rawPoint.y,
        globalStores.dynamicAbsorbDistance
      );
      return this._dxfAdsorbSegHandler(dxfLine);
    }
  }

  private _calculateDxfAdsorbByLine(startPoint: IPoint2d, endPoint: IPoint2d) {
    if (globalStores.paramStore.floorPlanConfig && globalStores.paramStore.floorPlanConfig.type === 'dxf') {
      const dxfLine = modelManager.floorPlanManager.getNearestParallelLineByLine(
        startPoint,
        endPoint,
        globalStores.dynamicAbsorbDistance
      );
      return this._dxfAdsorbSegHandler(dxfLine);
    }
  }

  private _updateSeparateWallData(point: IPoint2d) {
    if (this._separateWallRoomLocked) return;

    const roomId = getRoomId(globalStores.roomStore.getRoomDatas(), point);
    if (!roomId || roomId === this._separateWallRoomId) return;

    this._separateWallRoomId = roomId;
    this.updateWallData();
  }

  calculateAbsorbPointByLineThenRender(startPoint: IPoint2d, endPoint: IPoint2d, rawPoint: IPoint2d) {
    this.clearModel();

    // 计算新的墙体线段
    const shadowWallPosition = getOffsetLineSegByPoint(startPoint, endPoint, rawPoint);
    const calculateOptions: ICalculateOptions = {};

    // dxf线吸附
    if (this._type === EEntityType.Wall) {
      calculateOptions.extraWallInfos = this._calculateDxfAdsorbByLine(
        shadowWallPosition.start,
        shadowWallPosition.end
      );
    }

    const absorbPointInfo = this._calculator.calculateAbsorbByLineSeg(
      shadowWallPosition.start,
      shadowWallPosition.end,
      calculateOptions
    );
    if (!absorbPointInfo) return;

    this.updateModel(absorbPointInfo);

    return absorbPointInfo.absorbPoint;
  }

  calculateArcAbsorbPointThenRender(params: { arcStart: IPoint2d; arcEnd: IPoint2d; rawPoint: IPoint2d }) {
    this.clearModel();

    const absorbPointInfo = this._calculator.calculateArcAbsorb(params);

    if (!absorbPointInfo) return;

    this.updateModel(absorbPointInfo);

    return absorbPointInfo.absorbPoint;
  }

  private updateModel(sublineInfos: IAbsorbPointInfo) {
    const sublines: { start: IPoint2d; end: IPoint2d; center?: IPoint2d; clockwise?: boolean }[] = [];
    const absorbPoint = sublineInfos.absorbPoint;

    sublineInfos.sublineInfos.forEach((item) => {
      switch (item.type) {
        case ESublineType.WallSeg: {
          if (item.shape instanceof ArcSeg2d) {
            const arc = item.shape;
            sublines.push({
              start: arc.start,
              end: arc.end,
              center: arc.center,
              clockwise: arc.clockwise
            });
          } else {
            const shape = item.shape as LineSeg2d;
            sublines.push({
              start: shape.start,
              end: shape.end
            });
          }
          break;
        }
        case ESublineType.StartNormalLine: {
          if (item.origin) {
            sublines.push({
              start: item.origin,
              end: absorbPoint
            });
          }
          break;
        }
        case ESublineType.EndNormalLine: {
          if (item.origin) {
            sublines.push({
              start: item.origin,
              end: absorbPoint
            });
          }
          break;
        }
        case ESublineType.WallExtensionLine: {
          if (item.origin) {
            sublines.push({
              start: item.origin,
              end: absorbPoint
            });
          }
          break;
        }
        case ESublineType.HorizontalLine: {
          const size = SCENE_SIZE * pixelRatio2d;
          const minX = Math.min(-size, absorbPoint.x - size / 100);
          const maxX = Math.max(size, absorbPoint.x + size / 100);
          sublines.push({
            start: { x: minX, y: absorbPoint.y },
            end: { x: maxX, y: absorbPoint.y }
          });
          break;
        }
        case ESublineType.VerticalLine: {
          const size = SCENE_SIZE * pixelRatio2d;
          const minY = Math.min(-size, absorbPoint.y - size / 100);
          const maxY = Math.max(size, absorbPoint.y + size / 100);
          sublines.push({
            start: { x: absorbPoint.x, y: minY },
            end: { x: absorbPoint.x, y: maxY }
          });
          break;
        }
        case ESublineType.Circle: {
          const circle = item.shape as Circle2d;
          const startPoint = circle.getPointOnCircle(0);
          const endPoint = circle.getPointOnCircle(Math.PI * 2 - 0.001);
          sublines.push({
            start: startPoint,
            end: endPoint,
            center: circle.center,
            clockwise: false
          });
          break;
        }
      }

      if (item.extra?.arcCenterline instanceof ArcSeg2d) {
        const arc = item.extra.arcCenterline;
        sublines.push({
          start: arc.start,
          end: arc.end,
          center: arc.center,
          clockwise: arc.clockwise
        });
      }
    });

    this._sublineModel.update(sublines);
    if (sublineInfos.absorbPointType === EAbsorbPointType.Line) {
      // 线吸附不展示吸附点
      this._wallAbsorbPoint.clear();
    } else {
      this._wallAbsorbPoint.update(absorbPoint, this._zoom, sublineInfos.absorbPointType);
    }
  }

  clearModel() {
    this._sublineModel.clear();
    this._wallAbsorbPoint.clear();
  }

  dispose() {
    this._eventDisposeSet.forEach((dispose) => dispose());
    this._eventDisposeSet.clear();
    this._sublineModel.dispose();
    this._wallAbsorbPoint.dispose();
  }
}
